import { readState } from "@/__main__/app-state.mjs";
import { appURLs } from "@/app/constants.mjs";
import { translateQueueType } from "@/game-lol/utils/translate-queue-type.mjs";
import TftColor from "@/game-tft/colors.mjs";
import {
  championNameMap,
  DEFAULT_SET,
  revivalSets,
  setDates,
} from "@/game-tft/constants.mjs";
import { styleToColor } from "@/game-tft/get-style-to-color.mjs";
import { fetchStaticDataAsync } from "@/game-tft/tft-fetch-static-data.mjs";
import orderArrayBy from "@/util/order-array-by.mjs";
import makeStrictKeysObject from "@/util/strict-keys-object.mjs";

const staticTftMediaURLs = makeStrictKeysObject({
  getItemKey(apiKey, set) {
    if (typeof apiKey !== "string") return "";
    if (!apiKey.length) return "";

    if (apiKey === "TFT_Item_EmptyBag") return "";

    const itemStaticData = readState.tft[set]?.items;
    if (!itemStaticData) return "";
    const result = Object.values(itemStaticData).find(
      (item) => item.apiKey?.toLowerCase() === apiKey.toLowerCase(),
    );

    return result?.key || apiKey;
  },
  getItemKeyById(id, set) {
    const items = readState.tft[set]?.items;
    if (!items || !id || !set) return id;
    const item = Object.values(items).find((i) => i.id === parseInt(id));
    if (typeof item === "undefined") return "";
    return item.key;
  },
  getItemImage(key, set) {
    if (
      typeof key !== "string" ||
      typeof set !== "string" ||
      !key.length ||
      !set.length
    )
      return "";
    return `${appURLs.CDN}/blitz/tft/items/${set}/${key}.webp`;
  },
  getChampionImage(key, set) {
    if (
      typeof key === "string" &&
      typeof set === "string" &&
      key.length &&
      set.length
    ) {
      if (set === "set1") {
        key = this.getChampName(key);
      }
      return `${appURLs.CDN}/blitz/tft/champion_squares/${set}/${key}.webp`;
    }
    if (typeof key === "string" && key.length) {
      key = this.getChampName(key);
      return `${appURLs.CDN}/blitz/tft/champion_squares/set1/${key}.webp`;
    }
    return "";
  },
  getAugmentImage(key, set) {
    if (
      typeof key === "string" &&
      typeof set === "string" &&
      key.length &&
      set.length &&
      readState.tft[set]?.augments
    ) {
      const icon = this.getAugmentIcon(key, set)?.split(".")[0]?.toLowerCase();
      return `${appURLs.CDN}/blitz/tft/augments/${set}/icons/${icon}.webp`;
    }
    return "";
  },
  getPortalImage(key, set) {
    if (
      typeof key === "string" &&
      typeof set === "string" &&
      key.length &&
      set.length
    ) {
      const portals = readState.tft[set]?.portals || {};
      return `${appURLs.CDN}/blitz/tft/portals/${set}/${portals[key]?.icon
        ?.split(".")[0]
        ?.toLowerCase()}.webp`;
    }
    return "";
  },
  getChampName,
  fixChampKey: getChampName,
  getChampKey(apiKey, set) {
    if (typeof apiKey !== "string" || !apiKey || !set) return apiKey;
    // const champions = readState.tft[set]?.champions;
    // if (!champions) return apiKey; // Is it even possible for this to be falsy?
    // for (const key in champions) {
    //   const champData = champions[key];
    //   if (champData[set]?.apiKey?.toLowerCase() !== apiKey.toLowerCase())
    //     continue;
    //   return champData.key;
    // }
    return apiKey;
  },
  getChampAPIKey(key, set) {
    const champions = readState.tft[set]?.champions;
    if (!key || !set || !champions) return key;
    return Object.entries(champions).find(
      ([_, data]) =>
        data.name?.toLowerCase().replace(/[^a-z]/g, "") ===
        key.toLowerCase().replace(/[^a-z]/g, ""),
    )?.[0];
  },
  getChampInfo(apiKey, set) {
    if (typeof apiKey !== "string" || !apiKey || !set) return apiKey;
    const champions = readState.tft[set]?.champions;
    if (!champions) return apiKey; // Is it even possible for this to be falsy?
    for (const key in champions) {
      const champData = champions[key];
      if (champData?.apiKey?.toLowerCase() !== apiKey.toLowerCase()) continue;
      return champData;
    }
    return apiKey;
  },
  getTraitKey(apiKey, set) {
    const traits = readState.tft[set]?.traits;
    if (!apiKey || typeof apiKey !== "string" || !set || !traits) return apiKey;
    const foundTrait = Object.values(traits).find(
      (traitData) => traitData.apiKey?.toLowerCase() === apiKey?.toLowerCase(),
    );
    const split = foundTrait?.apiKey?.split("_") || [];
    const lastElement = split[split.length - 1];
    return lastElement || foundTrait?.name.toLowerCase() || apiKey;
  },
  getTraitApiKey(key, set) {
    const traits = readState.tft[set]?.traits;
    if (!key || typeof key !== "string" || !set || !traits) return key;
    const foundTrait = Object.values(traits).find((traitData) => {
      const split = traitData.apiKey?.split("_");
      const lastElement = split[split.length - 1];
      return (
        lastElement.toLowerCase() === key?.toLowerCase() ||
        traitData.name?.toLowerCase() === key?.toLowerCase()
      );
    });
    return foundTrait?.apiKey;
  },
  getTraitTierFromCount(apiKey, count, set) {
    if (!apiKey || !set || !count || !readState.tft[set]?.traits) return 0;
    const traits = readState.tft[set].traits;
    const trait = Object.values(traits).find(
      (traitData) => traitData.apiKey?.toLowerCase() === apiKey?.toLowerCase(),
    );
    const bonuses = trait?.bonuses;
    const tier = Array.isArray(bonuses)
      ? bonuses?.findLast((bonus) => count >= bonus.needed)
      : 1;
    return (Number.isInteger(tier) && tier) || 1;
  },
  getTraitStyleFromCount(apiKey, count, set) {
    if (!apiKey || !set || !count || !readState.tft[set]?.traits) return 1;
    const traits = readState.tft[set].traits;
    const style = Object.values(traits)
      .find(
        (traitData) =>
          traitData.apiKey?.toLowerCase() === apiKey?.toLowerCase(),
      )
      ?.bonuses.findLast((bonus) => count >= bonus.needed)?.style;
    return (Number.isInteger(style) && style) || 1;
  },
  getTraitColorFromCount(apiKey, count, set) {
    if (!apiKey || !set || !count || !readState.tft[set]?.traits) return "gold";
    const traits = readState.tft[set].traits;
    const style = Object.values(traits)
      .find(
        (traitData) =>
          traitData.apiKey?.toLowerCase() === apiKey?.toLowerCase(),
      )
      ?.bonuses.findLast((bonus) => count >= bonus.needed)?.style;
    return (Number.isInteger(style) && styleToColor(style)) || "gold";
  },
  getTraitBonus(apiKey, set, tier) {
    if (!apiKey || !set || !tier || !readState.tft[set]?.traits) return null;
    const traits = readState.tft[set].traits;
    const style = Object.values(traits).find(
      (traitData) => traitData.apiKey?.toLowerCase() === apiKey?.toLowerCase(),
    )?.bonuses[parseInt(tier) - 1];
    return style;
  },
  getTraitsFromUnits(units, set) {
    const champions = readState.tft[set]?.champions;
    const setTraits = readState.tft[set]?.traits;
    if (!units.length || !set || !champions || !setTraits) return {};
    const traits = {};
    units.forEach((unit) => {
      const champKey = unit.name;
      const champData = champions[champKey];
      if (champData) {
        [...champData.class, ...champData.origin].forEach((trait) => {
          if (traits[trait]) {
            traits[trait] += 1;
          } else {
            traits[trait] = 1;
          }
        });
      }
    });
    const activeTraits = {};
    Object.keys(traits).forEach((trait) => {
      setTraits[trait]?.bonuses?.forEach((bonus) => {
        if (traits[trait] >= bonus.needed) {
          activeTraits[this.getTraitKey(trait, set)] = bonus.needed;
        }
      });
    });
    return activeTraits;
  },
  sortTraits(traits, _set) {
    let result;
    if (traits?.length && traits[0].style) {
      result = orderArrayBy(traits, "style", "desc").filter(
        (trait) => trait.style !== 0,
      );
    } else {
      result = traits.map((trait) => {
        return {
          ...trait,
          // tierCurrent: this.getTraitTierFromCount(
          //   trait?.name,
          //   trait.num_units,
          //   set,
          // ),
          // style: this.getTraitStyleFromCount(trait?.name, trait.num_units, set),
          style: trait.style,
          tierCurrent: trait.tier_current,
        };
      });
      result = orderArrayBy(result, "style", "desc").filter(
        (trait) => trait.tierCurrent !== 0,
      );
    }
    return result;
  },
  getClassAndOriginsFromTraits(traits) {
    if (!traits) return [{}, {}];
    const classes = Object.fromEntries(
      Object.entries(traits).filter(([_, trait]) => trait.type === "class"),
    );
    const origins = Object.fromEntries(
      Object.entries(traits).filter(([_, trait]) => trait.type === "origin"),
    );
    return [classes, origins];
  },
  getAugmentIcon(apiKey, set) {
    const setAugments = readState.tft[set]?.augments;
    if (!setAugments) return apiKey;
    for (const tier in setAugments) {
      for (const k in setAugments[tier]) {
        const data = setAugments[tier][k];
        if (data.key?.toLowerCase() === apiKey?.toLowerCase()) {
          return data.icon;
        }
      }
    }
    return apiKey;
  },
  getAugmentTier(key, set) {
    const setAugments = readState.tft[set]?.augments;
    let foundTier;
    Object.keys(setAugments).forEach((tier) => {
      if (setAugments[tier][key]) {
        foundTier = tier;
      }
    });
    return foundTier;
  },
  getChampElement(key) {
    let champElement;

    switch (true) {
      case key.includes("Lux"):
        champElement = key.replace("Lux", "");
        return champElement;
      case key.includes("Qiyana"):
        champElement = key.replace("Qiyana", "");
        if (champElement === "Woodland") champElement = "Mountain";
        return champElement;
      default:
        return (champElement = null);
    }
  },
  inferTraitsFromUnits({
    units = [],
    traitsStaticData = {},
    unitsStaticData = {},
  }) {
    const inferredTraits = [];
    if (!units.length) return inferredTraits;

    // Obj map to keep track of aggregate count of traits
    const traitTally = {};

    // Loop through units to count traits
    for (const unit of units) {
      const unitKey = unit.characterId || unit.name;
      const unitInfo =
        unitsStaticData[unitKey] ||
        Object.values(unitsStaticData ?? {}).find(
          (u) =>
            u.apiKey?.toLowerCase() === unitKey?.toLowerCase() ||
            u.name?.toLowerCase() === unitKey?.toLowerCase(),
        );
      if (!unitInfo) continue;

      const unitTraits = [...unitInfo.class, ...unitInfo.origin];

      for (const unitTrait of unitTraits) {
        if (traitTally[unitTrait]) {
          traitTally[unitTrait] += 1;
        } else {
          traitTally[unitTrait] = 1;
        }
      }
    }
    // Loop through traits count aggregate to determine
    // if the count met the requirements
    for (const trait of Object.entries(traitTally)) {
      const [traitKey, traitCount] = trait;
      const traitInfo = traitsStaticData[traitKey];
      if (!traitInfo) continue;

      const { bonuses } = traitInfo;
      let styleMet = 0;
      let unitsMet = 0;

      for (const bonus of bonuses) {
        if (traitCount >= bonus.needed) {
          styleMet = bonus.style;
          unitsMet = bonus.needed;
        }
      }

      inferredTraits.push({
        name: traitKey,
        style: styleMet,
        num_units: traitTally[traitKey],
        tierCurrent: styleMet,
        tierTotal: bonuses.length,
        tier_units_required: unitsMet,
      });
    }

    return inferredTraits;
  },
  getMatchSetByDate(matchDate, queueId, searchParams = new URLSearchParams()) {
    let revivalSet;
    if (queueId)
      revivalSet = revivalSets.find(
        (s) => parseInt(s.queueId) === parseInt(queueId),
      );
    if (revivalSet && !searchParams) {
      searchParams.set("set", revivalSet.set);
    }
    if (queueId && searchParams) {
      if (revivalSet) {
        fetchStaticDataAsync(searchParams, revivalSet.set, { skipStats: true });
        return revivalSet.set;
      }
    }

    let set = DEFAULT_SET;
    for (let i = 0; i < setDates.length; i++) {
      const date = setDates[i];
      if (new Date(matchDate || Date.now()) > new Date(date.date)) {
        set = date.set;
        break;
      }
    }

    return set;
  },
  getStatsKey({ rank, region, sort }) {
    return `${rank.toLowerCase()}_${region}_${sort}`;
  },
  getRarityFromTier(rarity) {
    return TftColor.rarityGradient[rarity] ?? "transparent";
  },
  getHyperRollRankImage(name) {
    return `${appURLs.CDN}/ranks/tft-hyper-roll/${name.toLowerCase()}.webp`;
  },
  translateQueueType,
  getCompanionIcon(contentId) {
    if (!contentId) contentId = "5897ad9f-4665-4372-8f3e-6c878adb8918";
    return `${appURLs.CDN}/blitz/companions/0x90/${contentId}.webp`;
  },
  getRankedMedal(tier, size) {
    if (!tier || tier === "NONE" || typeof tier !== "string") {
      return `${appURLs.CDN}/ranks/2023/unranked.webp`;
    }
    if (size && typeof size === "number") {
      return `${appURLs.CDN}/ranks/2023/${tier.toLowerCase()}.webp`;
    }
    return `${appURLs.CDN}/ranks/2023/${tier.toLowerCase()}.webp`;
  },
  getLegendKey(id, set) {
    const legends = readState.tft[set]?.legends?.[set];
    if (!legends || !id || !set || !legends) return null;
    return (
      Object.entries(legends).find(
        ([, legendData]) => parseInt(legendData?.id) === parseInt(id),
      )?.[0] || id
    );
  },
  getClassesAndOrigins(traits) {
    if (!traits) return [{}, {}];
    const classes = {};
    const origins = {};
    const other = {};
    Object.entries(traits).forEach(([apiKey, data]) => {
      if (data.type === "class") {
        classes[apiKey] = data;
      } else if (data.type === "origin") {
        origins[apiKey] = data;
      } else {
        other[apiKey] = data;
      }
    });
    return [classes, origins, other];
  },
});

function getChampName(key) {
  const name = (key || "").replace(/^TFT(?:.*?)_/i, "");
  return championNameMap[name] || name.replace("_", "").replace(/\s/g, "");
}

export default staticTftMediaURLs;
